import React from "react";
import { Text, Box, SimpleGrid, Flex, Button, VisuallyHidden, ListItem, List } from "@chakra-ui/react";
import { useHits, type UseHitsProps, useSearchBox } from "react-instantsearch";
import Link from "next/link";
import { Integration, IntegrationCardSkeleton, type IntegrationRecord } from "./integration";

const transformItems: UseHitsProps<IntegrationRecord>["transformItems"] = (items, { results }) => {
  if (results?.query?.length === 0) {
    return items.filter((item) => item.status !== "shadow");
  }

  return items;
};

type ResultsProps = {
  isLoading: boolean;
};

function IntegrationsSearchResults({ isLoading }: ResultsProps) {
  const { hits, results, sendEvent } = useHits<IntegrationRecord>({ transformItems });
  const { clear } = useSearchBox();
  const hasSearchQuery = results?.query && results.query.length > 0;

  // Skip showing the loading UI while there is a text search query to avoid a flickering effect when the hits don't change
  if (isLoading && !hasSearchQuery) {
    // Since the server state is populated with all integrations, we'll always have a set of hits even on initial render
    return (
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing="small" pt="tiny">
        {hits.slice(0, 30).map((hit) => (
          <IntegrationCardSkeleton key={hit.objectID} hit={hit} />
        ))}

        {/** Include a visually hidden list of links to all the integration landing pages for optimal SEO of initial page load. */}
        <VisuallyHidden>
          <List>
            {hits.map((hit) => (
              <ListItem key={hit.objectID}>
                <Link href={`integrations/${hit.type}s/${hit.slug}`}>{hit.name}</Link>
              </ListItem>
            ))}
          </List>
        </VisuallyHidden>
      </SimpleGrid>
    );
  }

  if (!results || results.nbHits === 0) {
    return (
      <>
        <Text textStyle="h4">No results found</Text>
        <Flex as={Text} align="center" gap={1} mt={4}>
          <Button
            variant="unstyled"
            fontSize="lg"
            textDecoration="underline"
            textUnderlineOffset={3}
            _hover={{ color: "new.green.700" }}
            onClick={clear}
          >
            Reset your filters
          </Button>{" "}
          <Text as="span" fontSize="lg">
            to continue exploring.
          </Text>
        </Flex>
      </>
    );
  }

  return (
    <Box pt="tiny" pb="big">
      <SimpleGrid as="ul" columns={{ base: 1, sm: 2, md: 3 }} spacing="small" listStyleType="none">
        {hits
          ?.filter((hit) => hit.status !== "shadow")
          .map((hit) => (
            <Box key={hit.objectID} as="li">
              <Integration hit={hit} onSendEvent={sendEvent} />
            </Box>
          ))}
        {hits
          ?.filter((hit) => hit.status === "shadow")
          .map((hit) => (
            <Box key={hit.objectID} as="li">
              <Integration hit={hit} onSendEvent={sendEvent} />
            </Box>
          ))}
      </SimpleGrid>
    </Box>
  );
}

export default IntegrationsSearchResults;
